import React, { useState, useEffect } from 'react';
import axios from 'axios';
import NewsSection from './NewsSection';
import Testimonials from './Testimonials';
import ContentSection from './ContentSection';
import InsuranceSection from './InsuranceSection';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStethoscope, faHandHoldingMedical, faBookMedical } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.NODE_ENV === 'production' 
  ? '' // Bruker en relativ sti i produksjon
  : 'http://localhost:5000'; // Beholder den gamle for lokal utvikling

// Hjelper-objekt for å koble ikon-navn fra databasen til de importerte ikonene
const iconMap = {
  stethoscope: faStethoscope,
  'hand-holding-medical': faHandHoldingMedical,
  'book-medical': faBookMedical,
};


function HomePage() {
  const [content, setContent] = useState({});
  const [carouselImages, setCarouselImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    axios.get(`${API_URL}/api/content/hjem`).then(res => {
      const organized = res.data.reduce((acc, item) => ({ ...acc, [item.element_id]: item }), {});
      setContent(organized);
      
      try {
        const imagesFromJson = JSON.parse(organized.hjem_carousel_images?.value || '[]');
        setCarouselImages(imagesFromJson);
      } catch (e) {
        console.error("Kunne ikke parse karusellbilder:", e);
        setCarouselImages([]);
      }
    });
        
  }, []);

  useEffect(() => {
    if (carouselImages.length > 1) {
      const timer = setTimeout(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % carouselImages.length);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [currentImageIndex, carouselImages.length]);

  const homeSections = Object.values(content).filter(item => item.type === 'section');

  return (
    <div className="homepage-wrapper">
      <Helmet>
      <title>Sunnfjord Naprapatsenter – Din naprapat i Førde</title>
      <meta name="description" content="Ekspert på muskel- og leddplager i Førde. Vi finner årsaken til smertene dine og gir deg en varig løsning. Bestill time i dag." />
    </Helmet>
      <section className="hero-section">
        {carouselImages.map((imageUrl, index) => (
          <div
            key={index}
            className={`hero-image ${index === currentImageIndex ? 'active' : ''}`}
            style={{ backgroundImage: `url(${imageUrl})` }}
          />
        ))}
        <div className="hero-overlay"></div>
        <div className="hero-content">
          {content.hero_tittel && <h1>{content.hero_tittel.value}</h1>}
          {content.hero_undertittel && <p>{content.hero_undertittel.value}</p>}
        </div>
      </section>

      {/* --- VIKTIG: Alt etterfølgende innhold er nå pakket inn i én container --- */}
      <div className="container">
        {/* Nyhetsseksjonen kommer først, uten ekstra avstand over seg */}
        <NewsSection />

        {/* Alle etterfølgende seksjoner får .page-section for å skape luft */}
        <section className="page-section info-boxes-section">
          <div className="info-boxes-container">
            <div className="card info-box">
              {content.infobox1_ikon && <FontAwesomeIcon icon={iconMap[content.infobox1_ikon.value]} size="2x" className="info-box-icon" />}
              <h3>{content.infobox1_tittel?.value}</h3>
              <p>{content.infobox1_tekst?.value}</p>
            </div>
            <div className="card info-box">
              {content.infobox2_ikon && <FontAwesomeIcon icon={iconMap[content.infobox2_ikon.value]} size="2x" className="info-box-icon" />}
              <h3>{content.infobox2_tittel?.value}</h3>
              <p>{content.infobox2_tekst?.value}</p>
            </div>
            <div className="card info-box">
              {content.infobox3_ikon && <FontAwesomeIcon icon={iconMap[content.infobox3_ikon.value]} size="2x" className="info-box-icon" />}
              <h3>{content.infobox3_tittel?.value}</h3>
              <p>{content.infobox3_tekst?.value}</p>
            </div>
          </div>
        </section>
        <section className="page-section">
          <InsuranceSection />
        </section>

        {homeSections.map((section, index) => (
          <ContentSection 
            key={section.id} 
            section={section} 
            altBg={index % 2 !== 0}
          />
        ))}

        <section className="page-section feature-section">
          <div className="feature-content-container">
            <div className="feature-text">
              <h2>{content.feature1_tittel?.value}</h2>
              <p>{content.feature1_tekst?.value}</p>
            </div>
            <div className="feature-image">
              <img src={content.feature1_bilde?.value} alt={content.feature1_tittel?.value} />
            </div>
          </div>
        </section>

        <section className="page-section feature-section alt-bg">
          <div className="feature-content-container reverse">
            <div className="feature-text">
              <h2>{content.feature2_tittel?.value}</h2>
              <p>{content.feature2_tekst?.value}</p>
            </div>
            <div className="feature-image">
              <img src={content.feature2_bilde?.value} alt={content.feature2_tittel?.value} />
            </div>
          </div>
        </section>
      </div>
      <Testimonials />
    </div>
  );
}

export default HomePage;